// ==================== SMS TEMPLATES PAGE ====================

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Search,
  Copy,
  Eye,
  MoreVertical,
  Megaphone,
  Bell,
  Shield,
  HeadphonesIcon,
  FileText,
  Hash,
  Sparkles,
  Smartphone,
  Clock,
  BarChart3,
  Type,
} from 'lucide-react';
import { 
  Button, 
  Card, 
  Badge, 
  Input, 
  Modal, 
  Select, 
  ConfirmDialog, 
  Dropdown, 
  Pagination 
} from '../../../components/ui';
import { 
  smsTemplatesService, 
  type SmsTemplate, 
  type SmsTemplateCategory,
  type CreateSmsTemplateData,
  type SmsTemplateVariable,
} from '../../../services/sms-templates.service';
import {
  SMS_TEMPLATE_CATEGORY_OPTIONS,
  SMS_TEMPLATE_FILTER_CATEGORY_OPTIONS,
  SMS_TEMPLATE_FILTER_STATUS_OPTIONS,
  SMS_STANDARD_VARIABLES,
  SMS_SINGLE_SEGMENT_LIMIT,
  SMS_UNICODE_SINGLE_LIMIT,
  calculateSmsSegments,
  extractSmsVariables,
  getSmsCharacterWarning,
} from '../../../constants';

// ==================== CATEGORY CONFIG ====================

const CATEGORY_CONFIG: Record<SmsTemplateCategory, { 
  icon: typeof Megaphone; 
  bgColor: string; 
  textColor: string; 
  borderColor: string;
  label: string;
}> = {
  MARKETING: { 
    icon: Megaphone, 
    bgColor: 'bg-purple-500/10', 
    textColor: 'text-purple-400', 
    borderColor: 'border-purple-500/20',
    label: 'Marketing' 
  },
  TRANSACTIONAL: { 
    icon: FileText, 
    bgColor: 'bg-blue-500/10', 
    textColor: 'text-blue-400', 
    borderColor: 'border-blue-500/20',
    label: 'Transactional' 
  },
  NOTIFICATION: { 
    icon: Bell, 
    bgColor: 'bg-amber-500/10', 
    textColor: 'text-amber-400', 
    borderColor: 'border-amber-500/20',
    label: 'Notification' 
  },
  AUTHENTICATION: { 
    icon: Shield, 
    bgColor: 'bg-emerald-500/10', 
    textColor: 'text-emerald-400', 
    borderColor: 'border-emerald-500/20',
    label: 'Authentication' 
  },
  CUSTOMER_SERVICE: { 
    icon: HeadphonesIcon, 
    bgColor: 'bg-cyan-500/10', 
    textColor: 'text-cyan-400', 
    borderColor: 'border-cyan-500/20',
    label: 'Support' 
  },
};

// ==================== CATEGORY BADGE COMPONENT ====================

const CategoryBadge = ({ category }: { category: SmsTemplateCategory }) => {
  const config = CATEGORY_CONFIG[category];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// ==================== TEMPLATE CARD COMPONENT ====================

interface TemplateItemProps {
  template: SmsTemplate;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onPreview: () => void;
}

const TemplateItem = ({ template, onEdit, onDelete, onDuplicate, onPreview }: TemplateItemProps) => {
  const dropdownItems = [
    { key: 'preview', label: 'Preview', icon: <Eye className="w-4 h-4" />, onClick: onPreview },
    { key: 'edit', label: 'Edit', icon: <Pencil className="w-4 h-4" />, onClick: onEdit },
    { key: 'duplicate', label: 'Duplicate', icon: <Copy className="w-4 h-4" />, onClick: onDuplicate },
    { key: 'delete', label: 'Delete', icon: <Trash2 className="w-4 h-4" />, onClick: onDelete, colorClass: 'text-red-400 hover:text-red-300' },
  ];

  const usedVariables = extractSmsVariables(template.content);
  const config = CATEGORY_CONFIG[template.category];
  const Icon = config.icon;

  return (
    <div className="group flex items-center gap-4 p-4 hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0">
      {/* Icon */}
      <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center shrink-0`}>
        <Icon className={`w-6 h-6 ${config.textColor}`} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h4 className="text-sm font-medium text-white truncate">{template.name}</h4>
          <Badge variant={template.isActive ? 'success' : 'default'} className="text-[10px]">
            {template.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <CategoryBadge category={template.category} />
        </div>
        
        <p className="text-xs text-(--color-muted-gray) mt-1 truncate max-w-lg">
          {template.content}
        </p>
        
        <div className="flex items-center gap-4 mt-2 text-xs text-(--color-muted-gray)">
          <span className="flex items-center gap-1">
            <Hash className="w-3 h-3" />
            {template.characterCount} chars
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            {template.segmentCount} segment{template.segmentCount !== 1 ? 's' : ''}
          </span>
          {usedVariables.length > 0 && (
            <span className="flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {usedVariables.length} variable{usedVariables.length !== 1 ? 's' : ''}
            </span>
          )}
          {template.usageCount > 0 && (
            <span className="flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              Used {template.usageCount}x
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="shrink-0">
        <Dropdown
          trigger={<MoreVertical className="w-4 h-4" />}
          items={dropdownItems}
          align="right"
        />
      </div>
    </div>
  );
};

// ==================== CHARACTER COUNTER COMPONENT ====================

const CharacterCounter = ({ content }: { content: string }) => {
  const { characterCount, segmentCount, isUnicode } = calculateSmsSegments(content);
  const warning = getSmsCharacterWarning(characterCount, segmentCount);
  const limit = isUnicode ? SMS_UNICODE_SINGLE_LIMIT : SMS_SINGLE_SEGMENT_LIMIT;
  const maxChars = limit * Math.max(segmentCount, 1);
  const percentage = Math.min((characterCount / maxChars) * 100, 100);

  const getProgressColor = () => {
    if (segmentCount > 3) return 'bg-amber-500';
    if (segmentCount > 1) return 'bg-blue-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
      {/* Progress bar */}
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mb-3">
        <div 
          className={`h-full transition-all duration-300 rounded-full ${getProgressColor()}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-4">
          <span className="text-gray-300">
            <span className="font-medium">{characterCount}</span>
            <span className="text-gray-500"> / {maxChars} characters</span>
          </span>
          <span className={`font-medium ${segmentCount > 3 ? 'text-amber-400' : segmentCount > 1 ? 'text-blue-400' : 'text-emerald-400'}`}>
            {segmentCount} SMS segment{segmentCount !== 1 ? 's' : ''}
          </span>
          {isUnicode && (
            <Badge variant="warning" className="text-[10px]">Unicode</Badge>
          )}
        </div>
        {warning && (
          <span className="text-amber-400 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" />
            {warning}
          </span>
        )}
      </div>
    </div>
  );
};

// ==================== VARIABLE PICKER COMPONENT ====================

interface VariablePickerProps {
  onInsert: (variable: string) => void;
  variables?: SmsTemplateVariable[];
}

const VariablePicker = ({ onInsert, variables = SMS_STANDARD_VARIABLES }: VariablePickerProps) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        <Sparkles className="inline w-4 h-4 mr-1" />
        Personalization Variables
      </label>
      <div className="p-3 bg-white/5 rounded-lg border border-white/10">
        <p className="text-xs text-gray-500 mb-3">Click to insert a variable into your message</p>
        <div className="flex flex-wrap gap-2">
          {variables.map((v) => (
            <button
              key={v.variable}
              type="button"
              onClick={() => onInsert(`{{${v.variable}}}`)}
              className="px-3 py-1.5 text-xs font-mono bg-primary-500/10 hover:bg-primary-500/20 text-primary-400 rounded-md border border-primary-500/20 hover:border-primary-500/40 transition-all"
              title={v.description}
            >
              {`{{${v.variable}}}`}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== CREATE/EDIT MODAL COMPONENT ====================

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: SmsTemplate | null;
  onSuccess: () => void;
}

const TemplateModal = ({ isOpen, onClose, template, onSuccess }: TemplateModalProps) => {
  const isEdit = !!template;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<CreateSmsTemplateData>({
    name: '',
    description: '',
    category: 'MARKETING',
    content: '',
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (template) {
        setFormData({
          name: template.name,
          description: template.description || '',
          category: template.category,
          content: template.content,
        });
      } else {
        setFormData({
          name: '',
          description: '',
          category: 'MARKETING',
          content: '',
        });
      }
      setError(null);
    }
  }, [isOpen, template]);

  const handleInsertVariable = (variable: string) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content + variable,
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }
    if (!formData.content.trim()) {
      setError('Message content is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (isEdit && template) {
        await smsTemplatesService.update(template.id, formData);
      } else {
        await smsTemplatesService.create(formData);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit SMS Template' : 'Create SMS Template'}
      size="lg"
      showCloseButton
    >
      <div className="p-6">
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm"
          >
            {error}
          </motion.div>
        )}

        {/* Form */}
        <div className="space-y-5">
          {/* Template Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Template Name <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <Type className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Welcome Message"
                maxLength={100}
                className="pl-10"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of when to use this template"
                maxLength={500}
                className="pl-10"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <Select
              label="Category"
              value={formData.category}
              onChange={(value) => setFormData(prev => ({ ...prev, category: value as SmsTemplateCategory }))}
              options={SMS_TEMPLATE_CATEGORY_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
            />
          </div>

          {/* Message Content */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <MessageSquare className="inline w-4 h-4 mr-1" />
              Message Content <span className="text-red-400">*</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Type your message here. Use {{name}}, {{company}} etc. for personalization."
              className="w-full h-32 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none text-sm"
              maxLength={1600}
            />
            <CharacterCounter content={formData.content} />
          </div>

          {/* Variable Picker */}
          <VariablePicker onInsert={handleInsertVariable} />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button variant="ghost" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEdit ? 'Saving...' : 'Creating...'}
                </>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Create Template'
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ==================== PREVIEW MODAL COMPONENT ====================

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: SmsTemplate | null;
}

const PreviewModal = ({ isOpen, onClose, template }: PreviewModalProps) => {
  if (!template) return null;

  // Generate preview with sample data
  let preview = template.content;
  for (const v of SMS_STANDARD_VARIABLES) {
    const placeholder = `{{${v.variable}}}`;
    preview = preview.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), v.example);
  }

  const { characterCount, segmentCount } = calculateSmsSegments(preview);
  const config = CATEGORY_CONFIG[template.category];
  const Icon = config.icon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Template Preview" size="md" showCloseButton>
      <div className="p-6">
        {/* Template Info */}
        <div className="flex items-start gap-4 mb-6">
          <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center shrink-0`}>
            <Icon className={`w-6 h-6 ${config.textColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-medium text-white truncate">{template.name}</h4>
            <div className="flex items-center gap-2 mt-1.5">
              <CategoryBadge category={template.category} />
              <Badge variant={template.isActive ? 'success' : 'default'} className="text-[10px]">
                {template.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Phone Preview */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
            Message Preview
          </label>
          <div className="bg-gray-900 rounded-xl p-4 border border-white/10">
            {/* Phone header */}
            <div className="flex items-center justify-center gap-1.5 mb-4 pb-3 border-b border-white/10">
              <Smartphone className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs text-gray-500 font-medium">SMS Preview</span>
            </div>
            
            {/* Message bubble */}
            <div className="flex justify-end">
              <div className="bg-emerald-600 text-white px-4 py-3 rounded-2xl rounded-br-sm max-w-[85%]">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{preview}</p>
                <div className="flex items-center justify-end gap-1.5 mt-2">
                  <Clock className="w-3 h-3 text-emerald-200/60" />
                  <span className="text-[10px] text-emerald-200/60">
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Characters</p>
            <p className="text-xl font-semibold text-white">{characterCount}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Segments</p>
            <p className="text-xl font-semibold text-white">{segmentCount}</p>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-white/10">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
};

// ==================== MAIN PAGE COMPONENT ====================

export default function SmsTemplatesPage() {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 12;

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SmsTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<SmsTemplate | null>(null);
  const [deleteTemplate, setDeleteTemplate] = useState<SmsTemplate | null>(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await smsTemplatesService.getAll({
        search: searchQuery || undefined,
        category: categoryFilter as SmsTemplateCategory || undefined,
        isActive: statusFilter ? statusFilter === 'true' : undefined,
        page: currentPage,
        limit: itemsPerPage,
      });

      setTemplates(result.data || []);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalItems(result.pagination?.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, categoryFilter, statusFilter, currentPage]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Clear success message after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handlers
  const handleCreateSuccess = () => {
    setSuccessMessage('SMS template created successfully');
    fetchTemplates();
  };

  const handleEditSuccess = () => {
    setSuccessMessage('SMS template updated successfully');
    fetchTemplates();
  };

  const handleDuplicate = async (template: SmsTemplate) => {
    try {
      await smsTemplatesService.duplicate(template.id);
      setSuccessMessage('SMS template duplicated successfully');
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate template');
    }
  };

  const handleDelete = async () => {
    if (!deleteTemplate) return;

    setIsDeletingTemplate(true);
    try {
      await smsTemplatesService.delete(deleteTemplate.id);
      setSuccessMessage('SMS template deleted successfully');
      setDeleteTemplate(null);
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-primary-400" />
            SMS Templates
          </h1>
          <p className="text-gray-400 mt-1">
            Create and manage reusable SMS message templates with personalization variables
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Success/Error Messages */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-400"
          >
            <CheckCircle className="w-4 h-4" />
            {successMessage}
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between text-red-400"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
            <button onClick={() => setError(null)}>
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search templates..."
                className="pl-10"
              />
            </div>
          </div>

          <div className="w-[180px]">
            <Select
              value={categoryFilter}
              onChange={(value) => {
                setCategoryFilter(value as string);
                setCurrentPage(1);
              }}
              options={SMS_TEMPLATE_FILTER_CATEGORY_OPTIONS}
              placeholder="All Categories"
            />
          </div>

          <div className="w-[150px]">
            <Select
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as string);
                setCurrentPage(1);
              }}
              options={SMS_TEMPLATE_FILTER_STATUS_OPTIONS}
              placeholder="All Status"
            />
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary-400 animate-spin" />
        </div>
      ) : !templates || templates.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No SMS templates found</h3>
          <p className="text-gray-400 mb-4">
            {searchQuery || categoryFilter || statusFilter
              ? 'Try adjusting your filters'
              : 'Create your first SMS template to get started'}
          </p>
          {!searchQuery && !categoryFilter && !statusFilter && (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          )}
        </Card>
      ) : (
        <>
          {/* Templates List */}
          <Card className="overflow-hidden">
            {templates.map((template) => (
              <TemplateItem
                key={template.id}
                template={template}
                onEdit={() => setEditingTemplate(template)}
                onDelete={() => setDeleteTemplate(template)}
                onDuplicate={() => handleDuplicate(template)}
                onPreview={() => setPreviewTemplate(template)}
              />
            ))}
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-400">
                Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} templates
              </p>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={itemsPerPage}
                onPageChange={setCurrentPage}
                onPageSizeChange={() => {}}
              />
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <TemplateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <TemplateModal
        isOpen={!!editingTemplate}
        onClose={() => setEditingTemplate(null)}
        template={editingTemplate}
        onSuccess={handleEditSuccess}
      />

      <PreviewModal
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        template={previewTemplate}
      />

      <ConfirmDialog
        isOpen={!!deleteTemplate}
        onClose={() => setDeleteTemplate(null)}
        onConfirm={handleDelete}
        title="Delete SMS Template"
        description={`Are you sure you want to delete "${deleteTemplate?.name}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        isLoading={isDeletingTemplate}
      />
    </div>
  );
}
